import client from '@/api/client';
import type { VotingRecordEntry } from '@/components/VotingRecordDots';

export type VoterElectionsResponse = {
  voter_id: number;
  first_name: string;
  last_name: string;
  elections: VotingRecordEntry[];
};

/**
 * The turf-scoped headline numbers behind `GET /voters/stats`
 * (ANALYTICS_READ_ROLES, region-filtered server-side — a scoped caller's
 * totals are their turf's, an unscoped caller's are the county's). Only the
 * fields consumers actually read are typed; the endpoint returns more.
 */
export type VoterStats = {
  total_voters: number;
  active_voters: number;
};

/** Headline voter counts for the caller's turf. `signal` for badge fetches. */
export async function fetchVoterStats(signal?: AbortSignal): Promise<VoterStats> {
  const res = await client.get<VoterStats>('/voters/stats', { signal });
  return res.data;
}

// Per-voter records are stable within a session and popups reopen often,
// so cache by voter id to avoid refetching the same record on every open.
const cache = new Map<number, VoterElectionsResponse>();

/**
 * Fetch a voter's identity + recent General/Primary/Special voting record.
 * PII-gated on the backend (GET /voters/{id}/elections) — the name rides
 * along so PII-free maps holding only voter ids can render the label too.
 */
export async function fetchVoterElections(
  voterId: number,
): Promise<VoterElectionsResponse> {
  const cached = cache.get(voterId);
  if (cached) return cached;
  const res = await client.get<VoterElectionsResponse>(
    `/voters/${voterId}/elections`,
  );
  cache.set(voterId, res.data);
  return res.data;
}

/** A polygon ring vertex in GeoJSON axis order: [lng, lat]. */
export type LngLat = [number, number];

export type VotersWithinResponse = {
  /** The honest server-side count of voters inside the polygon. */
  total: number;
  /** Matching ids, capped by the server — may be shorter than `total`. */
  voter_ids: number[];
  /** True when `voter_ids` was truncated relative to `total`. */
  truncated: boolean;
};

/**
 * Select every voter inside a lasso polygon (POST /voters/within).
 *
 * `polygon` MUST be in GeoJSON order — [lng, lat] — which is the transpose of
 * Leaflet's LatLng. Callers holding Leaflet points have to flip them.
 *
 * `filters` carries the caller's active filter set (same param names and
 * encoding as GET /voters) so the selection respects what the user has
 * already narrowed to, rather than silently selecting everything geographic.
 *
 * This is deliberately a server query and not a client-side hit test: the pin
 * set is capped at the server (MAX_MAP_PINS) and can drift from the DB between
 * fetches, so testing loaded markers could return a plausible-looking
 * undercount with nothing to indicate anything was missing.
 */
/**
 * One dot on the voters map — deliberately PII-free (no name, no address).
 * The map plots tens of thousands of these; the popup fetches the voter's
 * details by id on click instead of shipping them all up front.
 */
export type MapPin = {
  id: number;
  lat: number;
  lng: number;
  /** EngagementTier value ("hot", "super_voter", …) or null for unclassified. */
  tier: string | null;
  score: number;
};

export type MapPinsResponse = {
  pins: MapPin[];
  /** TRUE count of matching geocoded voters, even when `pins` is capped. */
  total: number;
  /** True when `pins` is a partial set (server cap, currently 50k). */
  truncated: boolean;
};

/**
 * Every voter matching the filter set, as map pins (GET /voters/map-pins).
 *
 * `filters` uses the same param names and encoding as GET /voters. This
 * replaced fetching a 500-row /voters page for the map — the county has ~80k
 * voters, and a map showing the first 500 implied its clusters were totals.
 */
export async function fetchMapPins(
  filters: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<MapPinsResponse> {
  const res = await client.get<MapPinsResponse>('/voters/map-pins', {
    params: filters,
    signal,
  });
  return res.data;
}

export async function fetchVotersWithin(
  polygon: LngLat[],
  filters: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<VotersWithinResponse> {
  const res = await client.post<VotersWithinResponse>(
    '/voters/within',
    // polygon last so a stray `polygon` key in the filter set can't clobber it
    { ...filters, polygon },
    { signal },
  );
  return res.data;
}

export type KnockbackHistoryPoint = {
  id: number;
  outcome: string;
  timestamp: string;
  notes: string | null;
  canvasser_name: string | null;
};

export type VoterKnockbacksResponse = {
  voter_id: number;
  first_name: string;
  last_name: string;
  total_knockbacks: number;
  latest_outcome: string | null;
  latest_timestamp: string | null;
  recent_history: KnockbackHistoryPoint[];
};

/**
 * Fetch a voter's door-knock (canvass) history for the drawer's Canvass Status
 * card. PII-gated (GET /voters/{id}/knockbacks). NOT cached — knock status can
 * change during a session, and stale "not yet knocked" would mislead.
 */
export async function fetchVoterKnockbacks(
  voterId: number,
): Promise<VoterKnockbacksResponse> {
  const res = await client.get<VoterKnockbacksResponse>(
    `/voters/${voterId}/knockbacks`,
  );
  return res.data;
}

/* ---------------------------------------------------------------------------
 * Knock-status overlay (POST /canvass/knock-status)
 *
 * Deliberately a SEPARATE call from GET /voters rather than extra columns on
 * it. That query is the hot path for the voter list, the map, and the lasso;
 * widening it for a map-only concern would cost every caller a knockback join
 * they do not want. As an overlay it is also skippable — a map colored by
 * engagement tier never asks for it.
 * ------------------------------------------------------------------------- */

export type KnockStatusEntry = {
  voter_id: number;
  /**
   * not_routed | other_route | routed_unknocked | talked | not_home | refused | moved
   *
   * The three un-knocked states are distinct on purpose — see `mapColors.ts`.
   */
  state: string;
  outcome: string | null;
  last_knocked_at: string | null;
  route_id: number | null;
};

export type KnockStatusResponse = {
  entries: KnockStatusEntry[];
  /** Per-state tally over the requested ids, for the legend. */
  counts: Record<string, number>;
  /** Requested ids the caller cannot see — a pin with no color must be explainable. */
  unknown_voter_ids: number[];
};

/** How these specific doors stand. Id mode — capped server-side at 500 ids. */
export async function fetchKnockStatus(
  voterIds: number[],
  candidateId: number | null,
  signal?: AbortSignal,
): Promise<KnockStatusResponse> {
  const res = await client.post<KnockStatusResponse>(
    '/canvass/knock-status',
    {
      voter_ids: voterIds,
      ...(candidateId != null ? { candidate_id: candidateId } : {}),
    },
    { signal },
  );
  return res.data;
}

/**
 * How every door matching a filter set stands — the overlay for a map whose
 * pins came from `fetchMapPins` with the same filters. The server resolves
 * the voter set with the same plumbing, order, and cap as /voters/map-pins,
 * so the entries cover exactly the pins the map fetched; sending the tens of
 * thousands of plotted ids back over the wire would exceed the id-mode cap.
 *
 * `filters` must be the filter params ONLY (no `voter_ids`) — the endpoint
 * 422s on a mixed request rather than guessing which half wins.
 */
export async function fetchKnockStatusForFilters(
  filters: Record<string, unknown>,
  candidateId: number | null,
  signal?: AbortSignal,
): Promise<KnockStatusResponse> {
  const res = await client.post<KnockStatusResponse>(
    '/canvass/knock-status',
    {
      ...filters,
      ...(candidateId != null ? { candidate_id: candidateId } : {}),
    },
    { signal },
  );
  return res.data;
}

/** The fields the map popup renders from GET /voters/{id}. */
export type VoterPopupDetail = {
  id: number;
  first_name: string;
  last_name: string;
  address: string;
  engagement_tier: string | null;
  score: number;
  precinct_num: string | null;
};

// Popup details are stable within a session and pins get re-clicked, so cache
// by voter id — the same trade fetchVoterElections makes above.
const popupDetailCache = new Map<number, VoterPopupDetail>();

/**
 * Fetch one voter's popup details on demand. Map pins are PII-free by design
 * (see MapPin); this is the click-time counterpart that fetches the name and
 * address for exactly the one door someone asked about.
 */
export async function fetchVoterPopupDetail(
  voterId: number,
): Promise<VoterPopupDetail> {
  const cached = popupDetailCache.get(voterId);
  if (cached) return cached;
  const res = await client.get<VoterPopupDetail>(`/voters/${voterId}`);
  popupDetailCache.set(voterId, res.data);
  return res.data;
}
